import { useEffect } from 'react';

import type { FileError } from 'react-dropzone';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { ritualWindowOptions } from '../constants/ritualHours';
import { DAYS_OF_THE_WEEK } from 'constants/dateAndTime';

import {
    GetDetailedRitualQuery,
    RitualRepetition,
    SiteState,
    SiteType,
    TagState,
    useGetSupplierPartnershipsQuery,
    useGetActiveOrganizationsQuery,
    useGetActiveCategoriesQuery,
    useGetSubcategoriesQuery,
    useGetAvailableForMicrostoreQuery,
    useGetBadgesQuery,
    useGetMessagesSetsQuery,
    useGetTagsQuery,
    useGetSitesQuery,
} from 'data/__generated__';

import {
    DetailFormCheckbox,
    DetailFormDatePicker,
    DetailFormImage,
    DetailFormSelect,
    DetailFormTextArea,
    DetailFormValue,
} from 'components/DetailsView';
import { FileWithPreview } from 'components/FileDragAndDrop';
import { Loader } from 'components/Loader';
import { TotemLabel } from 'components/TotemLabel';
import { CopyValue } from 'components/CopyValue';

import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';
import { ColumnsSectionContainer, SectionColumn } from 'components/DetailsView/Section';

const defaultActiveWeekdays = [true, true, true, true, true, false, false];

const defaultRitual = {
    _id: null,
    activeDate: null,
    activeWeekdays: defaultActiveWeekdays,
    associatedDiscount: null,
    billToOrganizationId: null,
    expiringProductsOnly: false,
    imageUrl: null,
    messagesSetId: null,
    name: null,
    repetition: RitualRepetition.Weekly,
    requiresParticipation: false,
    resetsBetweenPurchases: false,
    restrictedBadgeIds: [],
    restrictedCategoryIds: [],
    restrictedOrganizationIds: [],
    restrictedProductIds: [],
    restrictedSiteIds: [],
    restrictedSubcategoryIds: [],
    restrictedTagIds: [],
    restrictedUserIds: [],
    timeWindow: { start: '10:00', end: '12:00' },
    supplierPartnershipId: null,
};

export const RitualFormContent = ({
    ritual,
    chosenTemplate,
}: {
    ritual?: GetDetailedRitualQuery['detailedRitual'] | null;
    chosenTemplate?: { name: string; imageUrl: string } | null;
}) => {
    const {
        _id,
        activeDate,
        activeWeekdays,
        associatedDiscount,
        billToOrganizationId,
        expiringProductsOnly,
        imageUrl,
        messagesSetId,
        name,
        repetition,
        requiresParticipation,
        resetsBetweenPurchases,
        restrictedBadgeIds,
        restrictedCategoryIds,
        restrictedOrganizationIds,
        restrictedProductIds,
        restrictedSiteIds,
        restrictedSubcategoryIds,
        restrictedTagIds,
        restrictedUserIds,
        timeWindow,
        supplierPartnershipId,
    } = ritual ?? defaultRitual;

    async function fileValidationFunction(file: FileWithPreview) {
        return new Promise<FileError[]>((resolve, reject) => {
            const image = new Image();
            image.onload = function () {
                const widthErrors =
                    image.width >= 600 && image.height >= 400
                        ? []
                        : [
                              {
                                  message: `L'image doit avoir une dimension d'au moins 600x400px, votre image fait ${image.width}x${image.height}px`,
                                  code: 'file-invalid-dimension',
                              },
                          ];
                const fileErrors = [...widthErrors];
                resolve(fileErrors);
            };
            image.onerror = reject;
            image.src = file.preview;
        });
    }

    const { control, register, setValue } = useFormContext();

    useEffect(() => {
        if (chosenTemplate) {
            setValue('name', chosenTemplate.name, { shouldDirty: true });
        }
    }, [chosenTemplate, setValue]);

    const repetitionWatch = useWatch({
        control,
        name: 'repetition',
        defaultValue: repetition,
    });

    const {
        loading: organizationsLoading,
        data: organizationsData,
        error: organizationsError,
    } = useGetActiveOrganizationsQuery();

    const {
        loading: activeCategoriesLoading,
        data: activeCategoriesData,
        error: activeCategoriesError,
    } = useGetActiveCategoriesQuery();

    const {
        loading: activeSubcategoriesLoading,
        data: activeSubcategoriesData,
        error: activeSubcategoriesError,
    } = useGetSubcategoriesQuery();

    const { loading: productsLoading, data: productsData, error: productsError } = useGetAvailableForMicrostoreQuery();

    const { loading: badgesLoading, data: badgesData, error: badgesError } = useGetBadgesQuery();

    const {
        loading: messagesSetsLoading,
        data: messagesSetsData,
        error: messagesSetsError,
    } = useGetMessagesSetsQuery();

    const { loading: tagsLoading, data: tagsData, error: tagsError } = useGetTagsQuery();

    const { loading: sitesLoading, data: sitesData, error: sitesError } = useGetSitesQuery();

    const {
        loading: supplierPartnershipsLoading,
        data: supplierPartnershipsData,
        error: supplierPartnershipsError,
    } = useGetSupplierPartnershipsQuery();

    if (
        organizationsLoading ||
        sitesLoading ||
        productsLoading ||
        activeCategoriesLoading ||
        activeSubcategoriesLoading ||
        badgesLoading ||
        tagsLoading ||
        messagesSetsLoading ||
        supplierPartnershipsLoading
    ) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (productsError || !productsData) {
        throw new Error('Une erreur est survenue lors de la récupération des produits');
    }

    if (activeCategoriesError || !activeCategoriesData) {
        throw new Error('Une erreur est survenue lors de la récupération des catégories actives');
    }

    if (activeSubcategoriesError || !activeSubcategoriesData) {
        throw new Error('Une erreur est survenue lors de la récupération des sous-catégories actives');
    }

    if (badgesError || !badgesData) {
        throw new Error('Une erreur est survenue lors de la récupération des badges');
    }

    if (messagesSetsError || !messagesSetsData) {
        throw new Error('Une erreur est survenue lors de la récupération des séries de messages');
    }

    if (tagsError || !tagsData) {
        throw new Error('Une erreur est survenue lors de la récupération des tags');
    }

    if (organizationsError || !organizationsData) {
        throw new Error('Une erreur est survenue lors de la récupération des organisations');
    }

    if (sitesError || !sitesData) {
        throw new Error('Une erreur est survenue lors de la récupération des sites');
    }

    if (supplierPartnershipsError || !supplierPartnershipsData) {
        throw new Error('Une erreur est survenue lors de la récupération des partenariats fournisseurs');
    }

    const { activeOrganizations: organizations } = organizationsData;
    const { sites } = sitesData;
    const { availableForMicrostore: products } = productsData;
    const { activeCategories } = activeCategoriesData;
    const { subcategories } = activeSubcategoriesData;
    const { badges } = badgesData;
    const { messagesSets } = messagesSetsData;
    const { tags } = tagsData;
    const { supplierPartnerships } = supplierPartnershipsData;

    const organizationOptions = organizations.map(({ _id, name }) => ({
        label: name,
        value: _id,
    }));

    const siteOptions = sites.map(({ _id, name, state, type }) => {
        const suffixes: string[] = [];
        if (state === SiteState.Archived) {
            suffixes.push('archivé');
        }
        if (type !== SiteType.Microstore) {
            suffixes.push(type);
        }
        return {
            label: suffixes.length ? `${name} (${suffixes.join(' — ')})` : name,
            value: _id,
        };
    });

    const repetitionOptions = [
        { label: 'Ponctuel', value: RitualRepetition.Punctual },
        { label: 'Hebdomadaire', value: RitualRepetition.Weekly },
    ];

    const productOptions = products.map((product) => {
        return { value: product?._id, label: product?.fullname };
    });

    const categoryOptions = activeCategories.map((category) => {
        return { value: category?._id, label: category?.title };
    });

    const subcategoryOptions = subcategories.map((subcategory) => {
        return { value: subcategory._id, label: subcategory.name };
    });

    const badgeOptions = badges.map((badge) => {
        return { value: badge?._id, label: badge?.name };
    });

    const tagOptions = tags
        // TODO: only fetch active tags
        .filter((tag) => tag.state === TagState.Regular)
        .map(({ _id, name }) => {
            return { value: _id, label: name };
        });

    const messagesSetsOptions = messagesSets.map((messagesSet) => ({
        label: messagesSet.name,
        value: messagesSet._id,
    }));

    const supplierPartnershipsOptions = supplierPartnerships.map((supplierPartnership) => ({
        label: supplierPartnership.name,
        value: supplierPartnership._id,
    }));

    const today = new Date();

    return (
        <Container>
            <ColumnsSectionContainer numberOfColumns={2}>
                <SectionColumn>
                    <Row>
                        <DetailFormImage
                            name="image"
                            label="Image"
                            defaultValue={imageUrl || chosenTemplate?.imageUrl}
                            fileValidationFunction={fileValidationFunction}
                            size={250}
                            required={!imageUrl && !chosenTemplate?.imageUrl}
                        />
                        <Column>
                            <DetailFormValue
                                label="Nom"
                                defaultValue={name}
                                {...register('name', { required: true })}
                            />
                            <DetailFormValue
                                label="Réduction associée"
                                sublabel="En €. Laissez ce champ vide si le rituel n'est pas lié à une réduction."
                                placeholder="Réduction en €"
                                defaultValue={associatedDiscount}
                                type="number"
                                width="100%"
                                step=".01"
                                {...register('associatedDiscount', {
                                    min: 0.01,
                                    valueAsNumber: true,
                                })}
                            />
                            <DetailFormCheckbox
                                label="Ce rituel requiert une participation pour accéder au crédit"
                                name="requiresParticipation"
                                defaultChecked={requiresParticipation}
                            />
                            <DetailFormCheckbox
                                label="Montant rechargé à chaque achat"
                                sublabel="Le crédit est remis à son montant total après chaque achat (utilisable un nombre illimité de fois). Sinon, le crédit est partagé sur l'ensemble de la journée."
                                name="resetsBetweenPurchases"
                                defaultChecked={resetsBetweenPurchases}
                            />
                        </Column>
                    </Row>
                </SectionColumn>
                <SectionColumn>
                    {_id ? <CopyValue value={_id} /> : null}
                    <DetailFormSelect<string>
                        label="Récurrence"
                        placeholder="Sélectionnez un mode de récurrence"
                        defaultValue={repetition}
                        name="repetition"
                        width="100%"
                        options={repetitionOptions}
                    />
                    <div>
                        {repetitionWatch === RitualRepetition.Weekly ? (
                            <div className="flex items-center gap-4">
                                {(activeWeekdays || defaultActiveWeekdays).map((weekday, index) => (
                                    <div
                                        key={DAYS_OF_THE_WEEK[index].name}
                                        className="flex flex-col items-center gap-1"
                                    >
                                        <TotemLabel>{DAYS_OF_THE_WEEK[index].label[0]}</TotemLabel>
                                        <input
                                            type="checkbox"
                                            defaultChecked={weekday}
                                            {...register(`activeWeekdays.${index}`)}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <DetailFormDatePicker
                                label="Date d'activation du rituel"
                                placeholder="Choisir une date"
                                defaultValue={activeDate}
                                name="activeDate"
                                control={control}
                                transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                                transformDateToValue={(date) =>
                                    date ? formatDateAsAnniversary({ dateTime: date }) : null
                                }
                                minDate={today}
                            />
                        )}
                    </div>
                </SectionColumn>
            </ColumnsSectionContainer>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <DetailFormSelect<string>
                        label="Début de l'horaire du rituel"
                        placeholder="Sélectionnez une heure"
                        defaultValue={timeWindow.start}
                        name="timeWindow.start"
                        options={ritualWindowOptions}
                    />
                    <DetailFormSelect<string>
                        label="Fin de l'horaire du rituel"
                        placeholder="Sélectionnez une heure"
                        defaultValue={timeWindow.end}
                        name="timeWindow.end"
                        options={ritualWindowOptions}
                    />
                    <DetailFormSelect<string>
                        label="Série de messages"
                        isClearable
                        placeholder="Sélectionnez une série de messages"
                        name="messagesSetId"
                        defaultValue={messagesSetId ?? undefined}
                        options={messagesSetsOptions}
                    />
                </SectionColumn>
                <SectionColumn>
                    <DetailFormSelect<string>
                        isMulti
                        label="Produits"
                        placeholder="Sélectionnez un ou plusieurs produit(s)"
                        defaultValue={restrictedProductIds}
                        isClearable
                        name="restrictedProductIds"
                        options={productOptions}
                    />
                    <DetailFormSelect<string>
                        isMulti
                        label="Catégories"
                        placeholder="Sélectionnez une ou plusieurs catégories(s)"
                        defaultValue={restrictedCategoryIds}
                        isClearable
                        name="restrictedCategoryIds"
                        options={categoryOptions}
                    />
                    <DetailFormSelect<string>
                        isMulti
                        label="Sous-catégories"
                        placeholder="Sélectionnez une ou plusieurs sous-catégories(s)"
                        defaultValue={restrictedSubcategoryIds}
                        isClearable
                        name="restrictedSubcategoryIds"
                        options={subcategoryOptions}
                    />
                    <DetailFormSelect<string>
                        isMulti
                        label="Badges"
                        placeholder="Sélectionnez un ou plusieurs badge(s)"
                        defaultValue={restrictedBadgeIds}
                        isClearable
                        name="restrictedBadgeIds"
                        options={badgeOptions}
                    />
                    <DetailFormSelect<string>
                        isMulti
                        label="Tags"
                        placeholder="Sélectionnez un ou plusieurs tag(s)"
                        defaultValue={restrictedTagIds}
                        isClearable
                        name="restrictedTagIds"
                        options={tagOptions}
                    />
                    <DetailFormCheckbox
                        label="Anti-gaspi"
                        sublabel="Uniquement pour les produits qui expirent le jour de rituel"
                        name="expiringProductsOnly"
                        defaultChecked={expiringProductsOnly}
                    />
                </SectionColumn>
                <SectionColumn>
                    <DetailFormSelect<string>
                        isMulti
                        label="Site(s) concerné(s)"
                        placeholder="Sélectionnez un ou plusieurs site(s)"
                        defaultValue={restrictedSiteIds}
                        isClearable
                        name="restrictedSiteIds"
                        options={siteOptions}
                    />
                    <DetailFormSelect<string>
                        isMulti
                        label="Organisation(s) concernée(s)"
                        placeholder="Sélectionnez une ou plusieurs organisation(s)"
                        defaultValue={restrictedOrganizationIds}
                        isClearable
                        name="restrictedOrganizationIds"
                        options={organizationOptions}
                    />
                    <DetailFormSelect<string>
                        label="Organisation pour la facturation"
                        sublabel="La consommation des crédits du rituel lui sera refacturée. Pas de refacturation si non renseigné."
                        placeholder="Sélectionnez une organisation"
                        defaultValue={billToOrganizationId ?? undefined}
                        name="billToOrganizationId"
                        options={organizationOptions}
                        isClearable
                    />
                    <DetailFormTextArea
                        label="Utilisateur(s) concerné(s)"
                        placeholder="Renseignez les identifiants des utilisateurs, séparés par des virgules"
                        defaultValue={restrictedUserIds.join(',')}
                        {...register('restrictedUserIds')}
                    />
                    <DetailFormSelect<string>
                        label="Trade marketing"
                        placeholder="Sélectionnez un trade marketing"
                        sublabel="Lorsque les crédits créés par tous les rituels liés atteignent le budget du trade marketing, les rituels liés seront automatiquement archivés."
                        defaultValue={supplierPartnershipId ?? undefined}
                        name="supplierPartnershipId"
                        options={supplierPartnershipsOptions}
                        isClearable
                    />
                </SectionColumn>
            </ColumnsSectionContainer>
        </Container>
    );
};

const Container = styled.div`
    margin-top: 10px;
    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
